import {
  AppointmentVariant,
  CANVAS_NOTE_TYPES,
  type CanvasAppointmentData,
} from "@rmh/defs";

/**
 * Canvas FHIR appointment statuses (the `appointmentStatus` string on
 * `CanvasAppointmentData`). The dashboard treats `booked`/`arrived`/`checked-in`
 * as upcoming, `fulfilled` as completed, and `noshow` as missed. `cancelled`
 * and `entered-in-error` appointments are already dropped server-side.
 */
export const CANVAS_UPCOMING_STATUSES = ["booked", "arrived", "checked-in"];
export const CANVAS_COMPLETED_STATUS = "fulfilled";
export const CANVAS_NO_SHOW_STATUS = "noshow";

export const isVirtualTriageReason = (
  appointment: Pick<CanvasAppointmentData, "appointmentReason">
): boolean => appointment.appointmentReason === "triage";

/**
 * Only virtual (telehealth) appointments are reschedulable; phone visits never
 * are. A completed (`fulfilled`) appointment can't be rescheduled either.
 */
export const canRescheduleCanvasAppointment = (
  appointment: Pick<
    CanvasAppointmentData,
    "variant" | "appointmentDateTime" | "appointmentStatus"
  >
): boolean => {
  if (appointment.variant !== AppointmentVariant.Virtual) {
    return false;
  }
  if (!appointment.appointmentDateTime) {
    return false;
  }
  return appointment.appointmentStatus !== CANVAS_COMPLETED_STATUS;
};

export const canCancelCanvasAppointment = (
  appointment: CanvasAppointmentData
): boolean => {
  if (!appointment.appointmentDateTime) {
    return false;
  }
  return CANVAS_UPCOMING_STATUSES.includes(appointment.appointmentStatus ?? "");
};

export type PatientExperienceVersion = "v1" | "v2";

const V2_NOTE_TYPE_CODES: readonly string[] = [
  CANVAS_NOTE_TYPES.virtualEvaluation.coding.code,
  CANVAS_NOTE_TYPES.phoneEvaluation.coding.code,
];

/**
 * A patient with any Evaluation appointment gets the v2 portal
 */
export const getPatientExperienceVersion = (
  appointments: Pick<CanvasAppointmentData, "noteType">[]
): PatientExperienceVersion =>
  appointments.some(({ noteType }) =>
    V2_NOTE_TYPE_CODES.includes(noteType ?? "")
  )
    ? "v2"
    : "v1";
